import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    SimpleGrid,
    Stack,
    Text,
    keyframes,
    useToast,
} from "@chakra-ui/react";
import {
    MdInfoOutline,
    MdRefresh,
    MdSettingsInputComponent,
    MdSpeaker,
} from "react-icons/md";

// Speakers = audio output, Line In = audio input.
const DEVICE_KINDS = {
    audiooutput: {label: "Speaker", plural: "Speakers", icon: MdSpeaker},
    audioinput: {label: "Line In", plural: "Line In", icon: MdSettingsInputComponent},
};

const RECENT_CHANGE_MS = 6000;

const dotPulse = keyframes`
    0%   { box-shadow: 0 0 0 0 var(--pulse-color); }
    70%  { box-shadow: 0 0 0 7px transparent; }
    100% { box-shadow: 0 0 0 0 transparent; }
`;

const cardFlash = keyframes`
    0%   { background-color: var(--flash-color); }
    100% { background-color: transparent; }
`;

const supportsDeviceApi = () =>
    typeof navigator !== "undefined" &&
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.enumerateDevices === "function";

const enumerateAudioDevices = async () => {
    if (!supportsDeviceApi()) {
        return [];
    }
    const list = await navigator.mediaDevices.enumerateDevices();
    return list.filter((device) => device.kind === "audiooutput" || device.kind === "audioinput");
};

const DeviceRow = ({device, now}) => {
    const meta = DEVICE_KINDS[device.kind];
    const connected = device.status === "connected";
    const isRecent = now - device.changedAt < RECENT_CHANGE_MS;
    const accent = connected ? "green" : "red";

    return (
        <Flex
            align="center"
            gap={3}
            p={3}
            borderRadius="12px"
            border="1px solid"
            borderColor={isRecent ? `${accent}.300` : "gray.100"}
            bg="white"
            transition="all 0.2s ease"
            _hover={{transform: "translateY(-2px)", boxShadow: "md", borderColor: `${accent}.200`}}
            sx={
                isRecent
                    ? {"--flash-color": `var(--chakra-colors-${accent}-50)`, animation: `${cardFlash} ${RECENT_CHANGE_MS}ms ease-out`}
                    : undefined
            }
        >
            <Flex
                w="40px"
                h="40px"
                flexShrink={0}
                borderRadius="10px"
                align="center"
                justify="center"
                bg={connected ? "teal.50" : "gray.100"}
                color={connected ? "teal.500" : "gray.400"}
            >
                <Icon as={meta.icon} boxSize="22px"/>
            </Flex>

            <Box flex="1" minW="0">
                <Text fontWeight="semibold" noOfLines={1}>{device.label}</Text>
                <Text fontSize="xs" color="gray.500">{meta.label}</Text>
            </Box>

            <HStack spacing={2} flexShrink={0}>
                <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={`${accent}.400`}
                    sx={{
                        "--pulse-color": `var(--chakra-colors-${accent}-400)`,
                        animation: `${dotPulse} 1.8s ease-out infinite`,
                    }}
                />
                <Badge colorScheme={accent} variant="subtle" borderRadius="full" px={2}>
                    {connected ? "Connected" : "Disconnected"}
                </Badge>
            </HStack>
        </Flex>
    );
};

const DeviceSection = ({kind, devices, now}) => {
    const meta = DEVICE_KINDS[kind];
    const connectedCount = devices.filter((device) => device.status === "connected").length;

    return (
        <Box>
            <Flex align="center" justify="space-between" mb={3}>
                <HStack>
                    <Icon as={meta.icon} color="teal.500" boxSize="20px"/>
                    <Text fontWeight="bold">{meta.plural}</Text>
                </HStack>
                <Badge colorScheme={connectedCount > 0 ? "green" : "red"} borderRadius="full" px={2}>
                    {connectedCount} connected
                </Badge>
            </Flex>

            {devices.length === 0 ? (
                <Flex
                    align="center"
                    gap={3}
                    p={4}
                    borderRadius="12px"
                    border="1px dashed"
                    borderColor="red.200"
                    bg="red.50"
                    color="red.500"
                >
                    <Box w="10px" h="10px" borderRadius="full" bg="red.400"/>
                    <Text fontSize="sm">No {meta.plural.toLowerCase()} detected.</Text>
                </Flex>
            ) : (
                <Stack spacing={3}>
                    {devices.map((device) => (
                        <DeviceRow key={device.id} device={device} now={now}/>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

const DevicesPanel = () => {
    const toast = useToast();
    const trackedRef = useRef({});
    const [tracked, setTracked] = useState({});
    const [supported] = useState(supportsDeviceApi);
    const [hasLabels, setHasLabels] = useState(false);
    const [now, setNow] = useState(Date.now());

    const reconcile = useCallback(
        (devices, {notify = false} = {}) => {
            const prev = trackedRef.current;
            const next = {...prev};
            const currentIds = new Set();
            const counters = {audiooutput: 0, audioinput: 0};
            const changes = [];
            let labelsSeen = false;

            devices.forEach((device, index) => {
                counters[device.kind] += 1;
                const meta = DEVICE_KINDS[device.kind];
                const id = `${device.kind}:${device.deviceId || device.groupId || `idx-${index}`}`;
                currentIds.add(id);

                if (device.label) {
                    labelsSeen = true;
                }
                const label = device.label || prev[id]?.label || `${meta.label} ${counters[device.kind]}`;
                const existing = prev[id];

                if (!existing || existing.status === "disconnected") {
                    next[id] = {id, kind: device.kind, label, status: "connected", changedAt: Date.now()};
                    if (existing) {
                        changes.push({type: "connected", label, kind: device.kind});
                    }
                } else {
                    next[id] = {...existing, label, status: "connected"};
                }
            });

            Object.values(prev).forEach((device) => {
                if (!currentIds.has(device.id) && device.status === "connected") {
                    next[device.id] = {...device, status: "disconnected", changedAt: Date.now()};
                    changes.push({type: "disconnected", label: device.label, kind: device.kind});
                }
            });

            trackedRef.current = next;
            setTracked(next);
            if (labelsSeen) {
                setHasLabels(true);
            }

            if (notify && changes.length) {
                changes.forEach((change) => {
                    toast({
                        title: `${DEVICE_KINDS[change.kind].label} ${change.type}`,
                        description: change.label,
                        status: change.type === "connected" ? "success" : "warning",
                        duration: 4000,
                        isClosable: true,
                        position: "top-right",
                    });
                });
            }
        },
        [toast]
    );

    const refresh = useCallback(
        async (notify = false) => {
            const devices = await enumerateAudioDevices();
            reconcile(devices, {notify});
        },
        [reconcile]
    );

    useEffect(() => {
        if (!supported) {
            return undefined;
        }

        refresh(false);

        const handleChange = () => refresh(true);
        navigator.mediaDevices.addEventListener("devicechange", handleChange);

        // Keep "recently changed" highlights and pulses in sync with the clock.
        const interval = setInterval(() => setNow(Date.now()), 1000);

        return () => {
            navigator.mediaDevices.removeEventListener("devicechange", handleChange);
            clearInterval(interval);
        };
    }, [supported, refresh]);

    const enableNames = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            stream.getTracks().forEach((track) => track.stop());
            await refresh(false);
        } catch (e) {
            toast({
                title: "Permission needed",
                description: "Allow microphone access to read device names.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    }, [refresh, toast]);

    const {speakers, lineIns, totalConnected} = useMemo(() => {
        const all = Object.values(tracked);
        const byChange = (a, b) => {
            if (a.status !== b.status) {
                return a.status === "connected" ? -1 : 1;
            }
            return b.changedAt - a.changedAt;
        };
        const speakerList = all.filter((d) => d.kind === "audiooutput").sort(byChange);
        const lineInList = all.filter((d) => d.kind === "audioinput").sort(byChange);
        return {
            speakers: speakerList,
            lineIns: lineInList,
            totalConnected: all.filter((d) => d.status === "connected").length,
        };
    }, [tracked]);

    if (!supported) {
        return (
            <Flex align="center" gap={3} p={4} borderRadius="12px" bg="orange.50" color="orange.600">
                <Icon as={MdInfoOutline} boxSize="22px"/>
                <Text fontSize="sm">
                    This browser cannot detect audio devices. Try the latest Chrome or Edge over HTTPS.
                </Text>
            </Flex>
        );
    }

    return (
        <Stack spacing={5}>
            <Flex
                align={{base: "flex-start", md: "center"}}
                justify="space-between"
                direction={{base: "column", md: "row"}}
                gap={3}
            >
                <HStack>
                    <Badge colorScheme="teal" borderRadius="full" px={3} py={1} fontSize="sm">
                        {totalConnected} device{totalConnected === 1 ? "" : "s"} connected
                    </Badge>
                </HStack>
                <HStack>
                    {!hasLabels && (
                        <Button size="sm" variant="outline" colorScheme="teal" onClick={enableNames}>
                            Show device names
                        </Button>
                    )}
                    <Button size="sm" leftIcon={<MdRefresh/>} colorScheme="teal" onClick={() => refresh(false)}>
                        Refresh
                    </Button>
                </HStack>
            </Flex>

            <SimpleGrid columns={{base: 1, lg: 2}} spacing={6}>
                <DeviceSection kind="audiooutput" devices={speakers} now={now}/>
                <DeviceSection kind="audioinput" devices={lineIns} now={now}/>
            </SimpleGrid>

            <Flex align="center" gap={2} color="gray.500">
                <Icon as={MdInfoOutline} boxSize="16px"/>
                <Text fontSize="xs">
                    Plug or unplug a speaker or line-in cable — changes are detected automatically.
                </Text>
            </Flex>
        </Stack>
    );
};

export default DevicesPanel;
